import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Tooltip, Input } from 'antd';
import { ArrowDownOutlined, InfoCircleOutlined, SearchOutlined, FolderOpenOutlined, CloseOutlined, PhoneOutlined } from '@ant-design/icons';
import { useAppel } from '../../components/appel/AppelProvider';
import { chercherDossiers, numerosCites, useOuvrirDossierSinistre } from './liensSinistre';
import Avatar from './Avatar';
import MessageBulle from './MessageBulle';
import ZoneSaisie from './ZoneSaisie';
import conversationService from '../../services/conversationService';
import stompClient from '../../services/stompClient';
import { T, construireLignes, derniereVueLibelle, ecrireBrouillon, genererUuid, lireBrouillon, texteSansMarqueurs } from './utils';
import { localeIntl } from '../../i18n/format';

/**
 * Colonne centrale : en-tête, fil, zone de saisie.
 *
 * ORDRE PAR SÉQUENCE. Le fil est un tableau trié par `sequence` ; les messages en
 * attente (pas encore acquittés) portent une séquence provisoire très grande pour
 * rester en bas, et sont remplacés par leur version serveur dès qu'elle arrive -
 * par REST (réponse du POST) ou par STOMP, celui qui arrive le premier.
 *
 * JAMAIS D'AUTO-DÉFILEMENT quand l'agent lit plus haut : un bouton « ↓ N nouveaux »
 * apparaît à la place. Le repère de lecture n'avance que si l'agent est en bas ET
 * que l'onglet a le focus.
 *
 * RECONNEXION : à chaque reprise STOMP, `GET ?depuis=<dernière séquence>` comble le
 * trou ; la fusion déduplique sur `messageTrackingId`, donc pas de doublon.
 */

const TAILLE_PAGE = 50;
const SEQ_PROVISOIRE = Number.MAX_SAFE_INTEGER / 2;

const fusionner = (courant, entrants) => {
  const parId = new Map();
  const parClient = new Map();
  courant.forEach((m) => { if (m.messageTrackingId) parId.set(m.messageTrackingId, m); if (m.clientUuid) parClient.set(m.clientUuid, m); });
  const resultat = [...courant];
  entrants.forEach((m) => {
    const existant = parId.get(m.messageTrackingId) || parClient.get(m.clientUuid);
    if (existant) {
      const i = resultat.indexOf(existant);
      resultat[i] = { ...m, etat: undefined };
    } else {
      resultat.push(m);
    }
  });
  resultat.sort((a, b) => (a.sequence - b.sequence) || ((a.creeLe || '') > (b.creeLe || '') ? 1 : -1));
  return resultat;
};

const Squelette = () => (
  <div style={{ padding: 16 }}>
    {[0, 1, 2, 3, 4, 5].map((i) => (
      <div key={i} style={{ display: 'flex', justifyContent: i % 3 === 2 ? 'flex-end' : 'flex-start', gap: 8, marginTop: 16 }}>
        {i % 3 !== 2 && <div style={{ width: 32, height: 32, borderRadius: '50%', background: T.fondBulle }} />}
        <div style={{ width: 180 + (i * 67) % 240, height: 44, borderRadius: T.rayon.carte, background: T.fondBulle }} />
      </div>
    ))}
  </div>
);

export default function FilConversation({
  conversation, moi, etatConnexion, surConversationMaj, panneauOuvert, surBasculerPanneau, surNonLusChange,
}) {
  const { t } = useTranslation('conversations');
  const navigate = useNavigate();
  const appelCtx = useAppel();
  const id = conversation?.conversationTrackingId;
  const [messages, setMessages] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [chargementAncien, setChargementAncien] = useState(false);
  const [debutAtteint, setDebutAtteint] = useState(false);
  const [premiereNonLue, setPremiereNonLue] = useState(null);
  const [nouveauxEnBas, setNouveauxEnBas] = useState(0);
  const [brouillon, setBrouillon] = useState('');
  const [reponseA, setReponseA] = useState(null);
  const [saisisseurs, setSaisisseurs] = useState({}); // id -> { nom, minuterie }
  const [recherche, setRecherche] = useState(null); // null | { q, resultats }
  const [glisse, setGlisse] = useState(0);
  const [surligne, setSurligne] = useState(null);

  const conteneur = useRef(null);
  const zoneSaisie = useRef(null);
  const enBasRef = useRef(true);
  const derniereSequenceRef = useRef(0);
  const positionInitialeFaite = useRef(false);
  const lectureEnvoyee = useRef(0);

  const estEnBas = () => {
    const el = conteneur.current;
    return el ? el.scrollHeight - el.scrollTop - el.clientHeight < 40 : true;
  };
  const versLeBas = useCallback((doux = false) => {
    const el = conteneur.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: doux ? 'smooth' : 'auto' });
  }, []);

  // ── Chargement initial ──────────────────────────────────────────────────
  useEffect(() => {
    if (!id) return undefined;
    let actif = true;
    setMessages([]); setChargement(true); setDebutAtteint(false); setPremiereNonLue(null);
    setNouveauxEnBas(0); setReponseA(null); setRecherche(null); setSaisisseurs({});
    setBrouillon(lireBrouillon(id));
    positionInitialeFaite.current = false;
    lectureEnvoyee.current = 0;
    conversationService.messages(id, { taille: TAILLE_PAGE }).then((p) => {
      if (!actif) return;
      setMessages(p.messages);
      setDebutAtteint(p.debutAtteint);
      setPremiereNonLue(p.premiereNonLue);
      derniereSequenceRef.current = p.derniereSequence;
    }).finally(() => actif && setChargement(false));
    return () => { actif = false; };
  }, [id]);

  // Position d'ouverture : premier non lu, sinon en bas.
  useLayoutEffect(() => {
    if (chargement || positionInitialeFaite.current || !messages.length) return;
    positionInitialeFaite.current = true;
    const el = conteneur.current;
    const cible = el?.querySelector('[data-separateur-nonlus]');
    if (cible && premiereNonLue != null) {
      cible.scrollIntoView({ block: 'center' });
    } else {
      versLeBas();
    }
    enBasRef.current = estEnBas();
  }, [chargement, messages.length, premiereNonLue, versLeBas]);

  useEffect(() => { derniereSequenceRef.current = Math.max(derniereSequenceRef.current, ...messages.filter((m) => m.sequence < SEQ_PROVISOIRE).map((m) => m.sequence), 0); }, [messages]);

  // ── Brouillon ───────────────────────────────────────────────────────────
  const surBrouillon = useCallback((t) => { setBrouillon(t); ecrireBrouillon(id, t); }, [id]);

  // ── Lecture ─────────────────────────────────────────────────────────────
  const marquerLu = useCallback(() => {
    if (!id || !document.hasFocus()) return;
    const seq = derniereSequenceRef.current;
    if (seq > lectureEnvoyee.current) {
      lectureEnvoyee.current = seq;
      conversationService.marquerLu(id, seq).then(() => surNonLusChange?.()).catch(() => { lectureEnvoyee.current = 0; });
    }
  }, [id, surNonLusChange]);

  useEffect(() => {
    if (chargement) return undefined;
    if (enBasRef.current) marquerLu();
    const surFocus = () => { if (estEnBas()) marquerLu(); };
    window.addEventListener('focus', surFocus);
    return () => window.removeEventListener('focus', surFocus);
  }, [chargement, messages, marquerLu]);

  // ── Défilement ──────────────────────────────────────────────────────────
  const chargerAnciens = useCallback(async () => {
    if (chargementAncien || debutAtteint || !messages.length) return;
    const premier = messages.find((m) => m.sequence < SEQ_PROVISOIRE);
    if (!premier) return;
    setChargementAncien(true);
    const el = conteneur.current;
    const hauteurAvant = el.scrollHeight;
    const topAvant = el.scrollTop;
    try {
      const p = await conversationService.messages(id, { avant: premier.sequence, taille: TAILLE_PAGE });
      setMessages((prev) => fusionner(prev, p.messages));
      setDebutAtteint(p.debutAtteint);
      // Ancre la position : pas de saut lors de l'insertion au-dessus.
      requestAnimationFrame(() => { el.scrollTop = topAvant + (el.scrollHeight - hauteurAvant); });
    } finally { setChargementAncien(false); }
  }, [chargementAncien, debutAtteint, messages, id]);

  const surDefilement = () => {
    const el = conteneur.current;
    if (!el) return;
    const enBas = estEnBas();
    enBasRef.current = enBas;
    if (enBas) { setNouveauxEnBas(0); marquerLu(); }
    if (el.scrollTop < 80) chargerAnciens();
  };

  // ── Temps réel ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!id) return undefined;
    const desabonner = stompClient.souscrire(`/topic/conversation/${id}`, (ev) => {
      const p = ev.payload;
      switch (ev.type) {
        case 'MESSAGE': {
          const etaitEnBas = enBasRef.current;
          setMessages((prev) => fusionner(prev, [p]));
          if (!p.parMoi && !etaitEnBas) setNouveauxEnBas((n) => n + 1);
          if (p.parMoi || etaitEnBas) requestAnimationFrame(() => versLeBas(true));
          if (!p.parMoi) setSaisisseurs((s) => { const c = { ...s }; delete c[p.auteurTrackingId]; return c; });
          break;
        }
        case 'MODIFICATION':
        case 'SUPPRESSION':
          setMessages((prev) => fusionner(prev, [p]));
          break;
        case 'SAISIE':
          if (p.utilisateurTrackingId === moi?.utilisateurTrackingId) break;
          setSaisisseurs((s) => {
            const c = { ...s };
            clearTimeout(c[p.utilisateurTrackingId]?.minuterie);
            if (p.enCours) {
              c[p.utilisateurTrackingId] = { nom: p.nom, minuterie: setTimeout(() => setSaisisseurs((x) => { const y = { ...x }; delete y[p.utilisateurTrackingId]; return y; }), 6000) };
            } else delete c[p.utilisateurTrackingId];
            return c;
          });
          break;
        case 'LECTURE':
          surConversationMaj?.((c) => ({
            ...c, membres: c.membres.map((m) => (m.utilisateurTrackingId === p.utilisateurTrackingId ? { ...m, derniereSequenceLue: p.sequence } : m)),
          }));
          break;
        case 'MEMBRES':
          surConversationMaj?.((c) => ({ ...c, membres: p }));
          break;
        default:
      }
    });
    return desabonner;
  }, [id, moi?.utilisateurTrackingId, surConversationMaj, versLeBas]);

  // Rattrapage après reconnexion : tout ce qui suit la dernière séquence connue.
  useEffect(() => {
    if (!id) return undefined;
    return stompClient.surReconnexion(async () => {
      try {
        const p = await conversationService.messages(id, { depuis: derniereSequenceRef.current, taille: 100 });
        if (p.messages.length) {
          const etaitEnBas = enBasRef.current;
          setMessages((prev) => fusionner(prev, p.messages));
          if (etaitEnBas) requestAnimationFrame(() => versLeBas());
          else setNouveauxEnBas((n) => n + p.messages.filter((m) => !m.parMoi).length);
        }
      } catch { /* la prochaine reconnexion réessaiera */ }
      reessayerEnAttente();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // ── Envoi ───────────────────────────────────────────────────────────────
  const envoyerAuServeur = useCallback(async (local) => {
    try {
      const r = await conversationService.envoyer(id, {
        clientUuid: local.clientUuid, contenu: local.contenu, priorite: local.priorite,
        replyToId: local.replyToId, pieceJointeIds: local.pieceJointeIds,
      });
      setMessages((prev) => fusionner(prev, [r]));
      surNonLusChange?.();
    } catch (e) {
      // Sans réseau : reste en attente et repartira à la reconnexion. Autre erreur : à réessayer à la main.
      const reseau = !e.response;
      setMessages((prev) => prev.map((m) => (m.clientUuid === local.clientUuid ? { ...m, etat: reseau ? 'attente' : 'erreur' } : m)));
    }
  }, [id, surNonLusChange]);

  const reessayerEnAttente = useCallback(() => {
    setMessages((prev) => {
      prev.filter((m) => m.etat === 'attente').forEach((m) => envoyerAuServeur(m));
      return prev;
    });
  }, [envoyerAuServeur]);

  const surEnvoyer = ({ contenu, priorite, pieceJointeIds, replyToId }) => {
    const local = {
      clientUuid: genererUuid(), messageTrackingId: null, sequence: SEQ_PROVISOIRE + Date.now(), contenu, priorite,
      pieceJointeIds, replyToId, type: 'TEXTE', parMoi: true, auteurTrackingId: moi?.utilisateurTrackingId,
      auteurNom: `${moi?.prenoms || ''} ${moi?.nom || ''}`.trim(), auteurInitiales: '', creeLe: new Date().toISOString(),
      etat: 'attente', piecesJointes: [], modifiable: false, supprime: false,
      reponseA: reponseA ? { messageTrackingId: reponseA.messageTrackingId, auteurNom: reponseA.auteurNom, extrait: reponseA.contenu, supprime: reponseA.supprime } : null,
    };
    setMessages((prev) => [...prev, local]);
    surBrouillon('');
    setReponseA(null);
    requestAnimationFrame(() => versLeBas());
    envoyerAuServeur(local);
  };

  const surModifier = async (m, contenu) => {
    try { const r = await conversationService.modifier(m.messageTrackingId, contenu); setMessages((prev) => fusionner(prev, [r])); } catch { /* toast global */ }
  };
  const surSupprimer = async (m) => {
    try { const r = await conversationService.supprimer(m.messageTrackingId); setMessages((prev) => fusionner(prev, [r])); } catch { /* toast global */ }
  };
  const surSaisie = useCallback((enCours) => { stompClient.publier(`/app/conversation/${id}/saisie`, { enCours }); }, [id]);
  const televerser = useCallback((fichier, opts) => conversationService.televerser(id, fichier, opts), [id]);

  const allerVers = (messageTrackingId) => {
    const el = conteneur.current?.querySelector(`[data-message="${messageTrackingId}"]`);
    if (el) {
      el.scrollIntoView({ block: 'center', behavior: 'smooth' });
      setSurligne(messageTrackingId);
      setTimeout(() => setSurligne(null), 1600);
    }
  };

  // ── Glisser-déposer sur toute la zone ───────────────────────────────────
  const surDragEnter = (e) => { e.preventDefault(); if (e.dataTransfer?.types?.includes('Files')) setGlisse((g) => g + 1); };
  const surDragLeave = (e) => { e.preventDefault(); setGlisse((g) => Math.max(0, g - 1)); };
  const surDrop = (e) => {
    e.preventDefault(); setGlisse(0);
    if (e.dataTransfer?.files?.length) zoneSaisie.current?.ajouterFichiers(e.dataTransfer.files);
  };

  // Bandeau de coupure : affiché seulement après un délai de grâce. Une
  // micro-coupure d'une seconde ne doit pas faire clignoter un avertissement -
  // un bandeau qui apparaît pour rien apprend à l'utilisateur à l'ignorer, et
  // il ne le lira plus le jour où il compte.
  const [coupureVisible, setCoupureVisible] = useState(false);
  useEffect(() => {
    if (etatConnexion === 'connecte') { setCoupureVisible(false); return undefined; }
    const t = setTimeout(() => setCoupureVisible(true), 2500);
    return () => clearTimeout(t);
  }, [etatConnexion]);

  // ── Recherche ───────────────────────────────────────────────────────────
  const ouvrirDossier = useOuvrirDossierSinistre();
  // Dossiers cités dans les messages CHARGÉS. Le fil est paginé : remonter plus
  // haut en découvre d'autres. Mieux vaut cette liste partielle, qui se complète
  // à mesure qu'on remonte, qu'un appel serveur de plus pour un repère de lecture.
  const dossiersCites = useMemo(() => numerosCites(messages), [messages]);

  // La recherche interroge DEUX sources : les messages de la discussion, et le
  // referentiel des sinistres. Chercher « 0363 » alors que personne ne l'a ecrit
  // dans le fil ne devait pas rester sans reponse - le dossier existe, il est
  // juste ailleurs. Les deux appels partent ensemble : l'un ne doit pas attendre
  // l'autre, et un echec cote sinistres (habilitation) laisse les messages.
  const lancerRecherche = async (q) => {
    const terme = (q || '').trim();
    if (terme.length < 2) { setRecherche({ q, resultats: [], dossiers: [] }); return; }
    const [resultats, dossiers] = await Promise.all([
      conversationService.rechercher(id, terme).catch(() => []),
      chercherDossiers(terme),
    ]);
    setRecherche({ q, resultats, dossiers });
  };

  // ── Rendu ───────────────────────────────────────────────────────────────
  const lignes = useMemo(() => construireLignes(messages, premiereNonLue), [messages, premiereNonLue]);
  const dernierEnvoye = useMemo(() => [...messages].reverse().find((m) => m.parMoi && !m.etat), [messages]);
  const lecteursDuDernier = useMemo(() => {
    if (!dernierEnvoye || !conversation) return [];
    return conversation.membres
      .filter((m) => m.actif && m.utilisateurTrackingId !== moi?.utilisateurTrackingId && m.derniereSequenceLue >= dernierEnvoye.sequence)
      .map((m) => m.nomComplet);
  }, [dernierEnvoye, conversation, moi]);

  if (!conversation) {
    return (
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: T.muet, fontSize: T.taille.corps, background: T.fond, textAlign: 'center', padding: 24, lineHeight: 1.5 }}>
        {t('fil.choisir1')}<br />{t('fil.choisir2')}
      </div>
    );
  }

  const interlocuteur = conversation.type === 'DIRECT' ? conversation.membres.find((m) => m.utilisateurTrackingId !== moi?.utilisateurTrackingId) : null;
  const sousTitre = interlocuteur
    ? (interlocuteur.enLigne ? t('presence.enLigne') : derniereVueLibelle(interlocuteur.derniereConnexion))
    : t('fil.membres', { count: conversation.membres.filter((m) => m.actif).length });
  const nomsSaisie = Object.values(saisisseurs).map((s) => s.nom);

  return (
    <div
      style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', background: T.blanc, position: 'relative' }}
      onDragEnter={surDragEnter} onDragOver={(e) => e.preventDefault()} onDragLeave={surDragLeave} onDrop={surDrop}
    >
      {/* En-tête */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', borderBottom: `1px solid ${T.bord}`, minHeight: 60 }}>
        <Avatar nom={conversation.titre} initiales={conversation.initiales} taille={40} presence={!!interlocuteur} enLigne={interlocuteur?.enLigne} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: T.taille.titre, fontWeight: 600, color: T.texte, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{conversation.titre}</div>
          <div style={{ fontSize: T.taille.meta, color: T.muet }}>{sousTitre}</div>
        </div>
        {conversation.dossierType === 'SINISTRE' && conversation.dossierTrackingId && (
          <button type="button" onClick={() => navigate(`/sinistres/${conversation.dossierTrackingId}`)}
            style={{ height: 32, padding: '0 12px', border: `1px solid ${T.bord}`, borderRadius: 16, background: T.vertXpale, color: T.vert, cursor: 'pointer', font: 'inherit', fontSize: T.taille.meta, fontWeight: 600 }}>
            <FolderOpenOutlined /> {t('fil.ouvrirDossier')}
          </button>
        )}
        {interlocuteur && appelCtx && (
          <Tooltip title={interlocuteur.enLigne ? t('fil.appelerNom', { nom: interlocuteur.nomComplet }) : t('fil.nonConnecte', { nom: interlocuteur.nomComplet })}>
            <button type="button" aria-label={t('fil.appeler')} disabled={appelCtx.etat !== 'inactif'}
              onClick={() => appelCtx.appeler(interlocuteur.utilisateurTrackingId, interlocuteur.nomComplet, id)}
              style={{
                height: 36, padding: '0 14px', border: 'none', borderRadius: 18, cursor: appelCtx.etat === 'inactif' ? 'pointer' : 'default',
                background: interlocuteur.enLigne ? T.vert : T.fondBulle, color: interlocuteur.enLigne ? '#fff' : T.muet,
                font: 'inherit', fontSize: T.taille.meta, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6,
              }}>
              <PhoneOutlined /> {t('fil.appeler')}
            </button>
          </Tooltip>
        )}
        <Tooltip title={t('fil.rechercherDans')}>
          <button type="button" aria-label={t('fil.rechercher')} onClick={() => setRecherche((r) => (r ? null : { q: '', resultats: [] }))}
            style={{ width: 36, height: 36, border: 'none', background: recherche ? T.vertPale : 'none', borderRadius: T.rayon.champ, cursor: 'pointer', color: recherche ? T.vert : T.muet }}>
            <SearchOutlined />
          </button>
        </Tooltip>
        <Tooltip title={t('fil.membresEtPieces')}>
          <button type="button" aria-label={t('fil.details')} onClick={surBasculerPanneau}
            style={{ width: 36, height: 36, border: 'none', background: panneauOuvert ? T.vertPale : 'none', borderRadius: T.rayon.champ, cursor: 'pointer', color: panneauOuvert ? T.vert : T.muet }}>
            <InfoCircleOutlined />
          </button>
        </Tooltip>
      </div>

      {/* Bandeau reconnexion */}
      {coupureVisible && (
        <div role="status" style={{
          background: T.ambreFond, color: T.ambre, fontSize: T.taille.meta, padding: '4px 16px',
          textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
        }}>
          <span>{t('fil.horsLigne')}</span>
          <button
            type="button" onClick={() => stompClient.reconnecter()}
            style={{
              font: 'inherit', fontSize: T.taille.mini, fontWeight: 600, color: T.ambre,
              background: 'transparent', border: `1px solid ${T.ambre}`, borderRadius: 12,
              padding: '1px 10px', cursor: 'pointer',
            }}
          >{t('fil.reessayer')}</button>
        </div>
      )}

      {/* Recherche */}
      {recherche && (
        <div style={{ borderBottom: `1px solid ${T.bord}`, background: T.fond, padding: '8px 16px' }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <Input.Search autoFocus placeholder={t('fil.rechercherPlaceholder')} allowClear enterButton onSearch={lancerRecherche} style={{ flex: 1 }} />
            <button type="button" aria-label={t('fil.fermerRecherche')} onClick={() => setRecherche(null)} style={{ width: 36, height: 36, border: 'none', background: 'none', cursor: 'pointer', color: T.muet }}><CloseOutlined /></button>
          </div>
          {dossiersCites.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <div style={{ fontSize: T.taille.mini, color: T.muet, marginBottom: 4 }}>
                {t('fil.dossiersCites')}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, maxHeight: 92, overflowY: 'auto' }}>
                {dossiersCites.map((d) => (
                  <button
                    key={d.numero} type="button"
                    // Cité par `#` : identifiant connu, ouverture directe. Écrit à la main : résolu.
                    onClick={() => (d.sinistreTrackingId
                      ? navigate(`/sinistres/${d.sinistreTrackingId}`)
                      : ouvrirDossier(d.numero))}
                    title={d.auteur ? t('fil.citePar', { auteur: d.auteur }) : t('fil.ouvrirDossier')}
                    style={{
                      font: 'inherit', fontSize: T.taille.meta, fontWeight: 600, color: T.vert,
                      background: T.vertXpale, border: `1px solid ${T.bord}`, borderRadius: 16,
                      padding: '3px 10px', cursor: 'pointer', whiteSpace: 'nowrap',
                    }}
                  >{d.numero}</button>
                ))}
              </div>
            </div>
          )}
          {recherche.q && recherche.dossiers?.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <div style={{ fontSize: T.taille.mini, color: T.muet, marginBottom: 4 }}>{t('fil.dossiersSinistres')}</div>
              {recherche.dossiers.map((d) => (
                <button key={d.sinistreTrackingId} type="button" onClick={() => navigate(`/sinistres/${d.sinistreTrackingId}`)}
                  style={{ display: 'block', width: '100%', textAlign: 'left', padding: '6px 8px', border: 'none', borderBottom: `1px solid ${T.bord}`, background: 'none', cursor: 'pointer', font: 'inherit', fontSize: T.taille.meta }}>
                  <span style={{ fontWeight: 600, color: T.vert }}>{d.numeroSinistreLocal}</span>
                  <span style={{ color: T.muet }}> · {d.statut}</span>
                  {d.assureNomComplet && (
                    <div style={{ color: T.texte2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.assureNomComplet}</div>
                  )}
                </button>
              ))}
            </div>
          )}
          {recherche.q && (
            <div style={{ maxHeight: 200, overflowY: 'auto', marginTop: 8 }}>
              <div style={{ fontSize: T.taille.mini, color: T.muet, marginBottom: 4 }}>{t('fil.messages')}</div>
              {recherche.resultats.length === 0 && <div style={{ fontSize: T.taille.meta, color: T.muet }}>{t('fil.aucunMessageCorrespond')}</div>}
              {recherche.resultats.map((r) => (
                <button key={r.messageTrackingId} type="button" onClick={() => allerVers(r.messageTrackingId)}
                  style={{ display: 'block', width: '100%', textAlign: 'left', padding: '6px 8px', border: 'none', borderBottom: `1px solid ${T.bord}`, background: 'none', cursor: 'pointer', font: 'inherit', fontSize: T.taille.meta }}>
                  <span style={{ fontWeight: 600, color: T.vert }}>{r.auteurNom}</span>
                  <span style={{ color: T.muet }}> · {new Date(r.creeLe).toLocaleDateString(localeIntl())}</span>
                  <div style={{ color: T.texte2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{texteSansMarqueurs(r.contenu)}</div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Fil */}
      <div
        ref={conteneur} role="log" aria-live="polite" aria-label={t('fil.messages')} onScroll={surDefilement}
        style={{ flex: 1, overflowY: 'auto', padding: '8px 24px 16px', minHeight: 0 }}
      >
        {chargement ? <Squelette /> : (
          <>
            {debutAtteint ? (
              <div style={{ textAlign: 'center', fontSize: T.taille.mini, color: T.faible, padding: '16px 0 8px' }}>{t('fil.debut')}</div>
            ) : (
              <div style={{ textAlign: 'center', fontSize: T.taille.mini, color: T.faible, padding: '8px 0' }}>{chargementAncien ? t('fil.chargement') : ' '}</div>
            )}
            {messages.length === 0 && (
              <div style={{ textAlign: 'center', color: T.muet, fontSize: T.taille.corps, padding: 32, lineHeight: 1.5 }}>
                {t('fil.aucunMessage1')}<br />{t('fil.aucunMessage2')}
              </div>
            )}
            {lignes.map((l) => {
              if (l.type === 'jour') {
                return (
                  <div key={l.cle} style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '16px 0 8px' }}>
                    <span style={{ flex: 1, height: 1, background: T.bord }} />
                    <span style={{ fontSize: T.taille.mini, color: T.muet }}>{l.libelle}</span>
                    <span style={{ flex: 1, height: 1, background: T.bord }} />
                  </div>
                );
              }
              if (l.type === 'nonlus') {
                return (
                  <div key={l.cle} data-separateur-nonlus style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '12px 0 4px' }}>
                    <span style={{ flex: 1, height: 1, background: T.rouge, opacity: 0.4 }} />
                    <span style={{ fontSize: T.taille.mini, color: T.rouge, fontWeight: 600 }}>{t('fil.nouveauxMessages')}</span>
                    <span style={{ flex: 1, height: 1, background: T.rouge, opacity: 0.4 }} />
                  </div>
                );
              }
              const m = l.message;
              return (
                // content-visibility : le navigateur ne rend que les lignes visibles, ce qui
                // tient un fil de plusieurs milliers de messages sans virtualisation maison.
                <div key={l.cle} data-message={m.messageTrackingId || undefined}
                  style={{ contentVisibility: 'auto', containIntrinsicSize: '0 56px', transition: 'background 0.6s', background: surligne && surligne === m.messageTrackingId ? T.ambreFond : 'transparent', borderRadius: T.rayon.carte }}>
                  <MessageBulle moiTrackingId={moi?.utilisateurTrackingId}
                    message={m} teteDeGroupe={l.teteDeGroupe}
                    dernierEnvoye={dernierEnvoye && m.messageTrackingId === dernierEnvoye.messageTrackingId}
                    lecteurs={lecteursDuDernier}
                    surRepondre={(x) => { setReponseA(x); zoneSaisie.current?.focus(); }}
                    surModifier={surModifier} surSupprimer={surSupprimer}
                    surReessayer={(x) => { setMessages((prev) => prev.map((y) => (y.clientUuid === x.clientUuid ? { ...y, etat: 'attente' } : y))); envoyerAuServeur(x); }}
                    surAllerVers={allerVers}
                    surRappeler={interlocuteur && appelCtx ? () => appelCtx.appeler(interlocuteur.utilisateurTrackingId, interlocuteur.nomComplet, id) : null}
                  />
                </div>
              );
            })}
          </>
        )}
      </div>

      {/* Saisie en cours */}
      <div style={{ height: 20, padding: '0 24px', fontSize: T.taille.mini, color: T.muet, fontStyle: 'italic' }} aria-live="polite">
        {nomsSaisie.length === 1 && t('fil.ecrit', { nom: nomsSaisie[0] })}
        {nomsSaisie.length > 1 && (nomsSaisie.length > 2
          ? t('fil.ecriventPlusieurs', { premier: nomsSaisie[0], second: nomsSaisie[1] })
          : t('fil.ecriventDeux', { premier: nomsSaisie[0], second: nomsSaisie[1] }))}
      </div>

      {nouveauxEnBas > 0 && (
        <button type="button" onClick={() => { versLeBas(true); setNouveauxEnBas(0); }}
          style={{
            position: 'absolute', bottom: 96, left: '50%', transform: 'translateX(-50%)', height: 36, padding: '0 16px', border: 'none',
            borderRadius: 18, background: T.vert, color: '#fff', cursor: 'pointer', font: 'inherit', fontSize: T.taille.meta, fontWeight: 600,
            boxShadow: '0 2px 6px rgba(0,0,0,0.12)', display: 'flex', alignItems: 'center', gap: 8,
          }}>
          <ArrowDownOutlined /> {t('fil.nouveauxEnBas', { count: nouveauxEnBas })}
        </button>
      )}

      {glisse > 0 && (
        <div style={{
          position: 'absolute', inset: 8, border: `2px dashed ${T.vert}`, borderRadius: T.rayon.carte, background: 'rgba(232,245,237,0.85)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', color: T.vert, fontSize: T.taille.titre, fontWeight: 600, pointerEvents: 'none', zIndex: 2,
        }}>
          {t('fil.deposer')}
        </div>
      )}

      <ZoneSaisie
        ref={zoneSaisie} conversationId={id} brouillon={brouillon} surBrouillon={surBrouillon} surEnvoyer={surEnvoyer}
        surSaisie={surSaisie} reponseA={reponseA} surAnnulerReponse={() => setReponseA(null)} televerser={televerser}
        membres={conversation?.membres || []} moiTrackingId={moi?.utilisateurTrackingId}
      />
    </div>
  );
}
